<template>
	<header class="header">
		<router-link class="logo" to="/">{{ siteName }}</router-link>

		<nav :class="{ menu: true, mini: !fullMenu }">
			<div class="action-menu">
				<div class="menu-item">
					<router-link to="/" title="Projects" exact @click.native="menuLinkClick">
						<Icon type="projects" class="svg" />
						<span class="label">Dashboard</span>
					</router-link>
				</div>

				<div :class="{ 'menu-item': true, 'has-sub-menu': hasRankTracking, 'rank-tracking': true, 'internal-icon': true }">
					<router-link to="/rank-tracking" title="Rank Tracking">
						<Icon type="rank-tracking" class="svg" />
						<span class="label">Rank Tracking</span>
					</router-link>
					<SubMenu class="menu-sub-items" pageType="RankTracking" :showHeading="!fullMenu" key="sub-1" @menuLinkClick="menuLinkClick" />
				</div>

				<div :class="{ 'menu-item': true, 'has-sub-menu': hasDataDive }">
					<router-link to="/data-dive" title="Data Dive">
						<Icon type="data-dive" class="svg" />
						<span class="label">Data Dive</span>
					</router-link>
					<SubMenu class="menu-sub-items" pageType="DataDive" key="sub-2" :showHeading="!fullMenu" @menuLinkClick="menuLinkClick" searchPlaceholder="Search domains..." />
				</div>

				<div class="menu-item">
					<router-link to="/investigate" title="Investigate">
						<Icon type="investigate" class="svg" />
						<span class="label">To Investigate</span>
					</router-link>
				</div>

				<div class="menu-item">
					<router-link to="/alerts/all" title="Alerts">
						<Icon type="alerts" class="svg" />
						<span class="label">Alerts</span>
					</router-link>
				</div>

				<div class="menu-item" v-if="showReports">
					<router-link to="/reports" title="Reports">
						<Icon type="reports" class="svg" />
						<span class="label">Reports</span>
					</router-link>
				</div>

				<div class="menu-item content-keywords two-line internal-icon">
					<router-link to="/content-and-keywords" title="Content &amp; Keywords">
						<Icon type="content-keywords" class="svg" />
						<span class="label">Content &amp; Keywords</span>
					</router-link>
				</div>

				<div class="menu-item">
					<router-link to="/users" title="Users" v-if="hasProjects">
						<Icon type="users" class="svg" />
						<span class="label">Users</span>
					</router-link>
					<span title="Users" v-if="!hasProjects" @click="noUsers">
						<Icon type="users" class="svg" />
						<span class="label">Users</span>
					</span>
				</div>

				<div class="menu-item">
					<span @click="loadVideos">
						<Icon type="video-youtube" class="svg" colour="#F95930" />
						<span class="label">Video Guides</span>
					</span>
				</div>

				<div class="menu-item minmax">
					<span @click="showFullMenu">
						<Icon type="show-menu" class="svg" />
						<span class="label">Mini Menu</span>
					</span>
				</div>
			</div>

			<div class="admin-menu">
				<div class="admin-menu-wrap">
					<div class="menu-item account">
						<a href="#" title="Profile" v-html="initials" class="initials" />
					</div>

					<div class="name-info">
						<span
							:class="{ active: shouldShowMenuPopout('account'), name: true }"
							v-html="name"
							@click="showMenuPopout('account', $event)"
						/>

						<div :class="{ 'menu-popout': true, show: shouldShowMenuPopout('account') }">
							<div class="link-wrap">
								<router-link to="/account/home/" title="Profile">
									<font-awesome-icon icon="user" :style="{ color: '#3b445b' }" />
									<span class="label">Profile</span>
								</router-link>
							</div>
							<div class="link-wrap">
								<router-link to="/account/subscriptions/" title="Subscriptions">
									<font-awesome-icon icon="dollar-sign" :style="{ color: '#3b445b' }" />
									<span class="label">Subscriptions</span>
								</router-link>
							</div>
							<div class="link-wrap">
								<router-link to="/account/invoices/" title="Invoices">
									<font-awesome-icon icon="file-invoice-dollar" :style="{ color: '#3b445b' }" />
									<span class="label">Invoices</span>
								</router-link>
							</div>
							<div class="link-wrap">
								<router-link to="/account/payment-methods/" title="Payment Methods">
									<font-awesome-icon icon="credit-card" :style="{ color: '#3b445b' }" />
									<span class="label">Payment Methods</span>
								</router-link>
							</div>
							<div class="link-wrap" v-if="!isShared">
								<router-link to="/account/google/" title="Payment Methods">
									<Icon type="dataDive" class="svg" />
									<span class="label">Google Accounts</span>
								</router-link>
							</div>
							<div class="link-wrap" v-if="showAdmin">
								<router-link to="/admin/users" title="Admin">
									<Icon type="admin" class="svg" />
									<span class="label">Admin</span>
								</router-link>
							</div>
							<div class="link-wrap">
								<span @click="logout($event)" class="menu-link"
									><font-awesome-icon icon="sign-out-alt" :style="{ color: '#3b445b' }" /><span class="label">Log out</span></span
								>
							</div>
						</div>
					</div>
				</div>
			</div>
		</nav>

		<button id="toggle-menu" @click="toggleMenu">
			<span class="text">Menu</span>
			<span class="bars"></span>
		</button>

		<div class="shared-accounts" v-if="sharedAccess.length > 0">
			<div>
				<v-select
					placeholder="Shared accounts"
					v-model="sharedAccount"
					:clearable="false"
					:options="[{ _id: 'me', label: 'Your account' }, ...sharedAccess]"
					:reduce="(option) => option._id"
				/>
			</div>
		</div>

		<AnalysisCredits v-if="!isPublic" />
	</header>
</template>

<script>
import Vue from "vue";
import { mapMutations, mapState, mapActions } from "vuex";
import store from "../store";
import general from "./../utils/general";
import { TokenService } from "./../services/storage.service";
import AnalysisCredits from "./AnalysisCredits.vue";
import SubMenu from "./Display/SubMenu.vue";
import Icon from "./Display/Icon.vue";
import Videos from "./Videos.vue";
export default {
	name: "Header",
	props: ["siteName", "fullMenu"],
	components: {
		AnalysisCredits,
		Videos,
		SubMenu,
		Icon,
	},
	data() {
		return {
			btnState: true,
			menuPopout: {},
			searchText: "",
			sharedAccount: "me",
			sharedLoaded: false,
			showInsightsMenuMobile: false,
		};
	},
	watch: {
		sharedAccount: function(after, b4) {
			this.sharedAccount = after;
			TokenService.saveSharedAccount(after);
			if (b4 !== after && this.sharedLoaded) {
				if (this.$route.params.projectId) {
					this.$router.push(`/${this.$route.path.split("/")[1]}/`);
				} else {
					location.reload();
				}
			}
		},
		$route(to, from) {
			this.menuPopout = {};
			this.$emit("mobileMenu", false);
		}
	},
	computed: {
		...mapState("general", ["projectList", "showVideos", "playVideo", "userInfo"]),

		initials() {
			return TokenService.getInitials();
		},
		sharedAccess() {
			return this.userInfo.sharedWith || [];
		},
		isShared() {
			return !!this.userInfo.isShared;
		},
		name() {
			return TokenService.getName();
		},
		hasRankTracking() {
			return this.projectList.find(project => project.hasRankTracking);
		},
		hasDataDive() {
			return this.projectList.find(project => project.hasDataDive);
		},
		hasInsights() {
			let h = false;

			this.projectList.forEach(function(project) {
				if (!!project.hasInsights) h = true;
			});

			return h;
		},
		analysisCredits() {},
		isDev() {
			return window.location.host !== "app.rankcaddy.io";
		},
		showReports() {
			let extra = TokenService.getExtraInfo();

			return extra.devAccess.indexOf("reports") >= 0 || this.showAdmin;
		},
		showAdmin() {
			let extra = TokenService.getExtraInfo();

			return extra && extra.access && extra.access === "admin";
		},
		isPublic() {
			return this.$route.meta.public;
		},
		hasProjects() {
			return this.projectList.length > 0;
		},
	},
	methods: {
		...mapMutations("general", ["updateVar"]),
		...mapActions("general", ["loadProjects"]),

		noInsights() {
			general.$snotify(this, "error", "Insights", "No insights are available at the moment.");
		},

		noUsers() {
			general.$snotify(this, "error", "Users", "Please add a project.");
		},

		handleInsightsClick() {

			if ( window.innerWidth > 767 && !!this.$route.params.projectId && this.$route.name !== "dashboard-insights-project") {
				this.$router.push("/insights/" + this.$route.params.projectId);
			} else {
				this.showInsightsMenuMobile = !this.showInsightsMenuMobile;
			}
		},

		loadVideos(e) {
			e.preventDefault();

			this.updateVar({ varName: "showVideos", info: true });
		},
		closeVideos() {
			this.updateVar({ varName: "showVideos", info: false });
			general.modalClose();
		},

		showAnalysis(type) {
			let extra = TokenService.getExtraInfo();

			return this.showAdmin || (extra.devAccess && extra.devAccess.indexOf(type) >= 0);
		},

		menuLinkClick() {
			this.$emit("menuLinkClick");
		},

		showFullMenu() {
			this.$emit("fullMenu");
		},
		toggleMenu() {
			this.$emit("mobileMenu");
		},
		logout(event) {
			event.preventDefault();
			this.updateVar({ varName: "mobileMenu", info: false });
			store.dispatch("auth/logout");
		},
		showMenuPopout(type, event) {
			event.preventDefault();
			var t = true;
			if (typeof this.menuPopout[type] !== "undefined" && this.menuPopout[type] === true) {
				t = false;
			}
			Vue.set(this.menuPopout, type, t);
		},
		shouldShowMenuPopout(type) {
			return typeof this.menuPopout[type] !== "undefined" && this.menuPopout[type] === true;
		},
	},
	mounted() {
		this.loadProjects();
		let uid = TokenService.getId();
		this.sharedAccount = TokenService.getSharedAccount();
		setTimeout(
			function() {
				this.sharedLoaded = true;
			}.bind(this),
			100
		);
		if (uid) {
			window.appziSettings = {
				userId: uid,
				data: {
					user: uid,
				},
			};
		}
	},
};
</script>

<style lang="less" scoped>
@import "./../assets/css/less/_helper.less";
@import "./../assets/css/less/_settings.less";
.disabledLink {
	pointer-events: none;
}
.shared-accounts {
	display: none;
	position: fixed;
	top: 7px;
	left: 180px;
	width: 200px;
	z-index: 3;

	> div {
		width: 100%;
	}

	span {
		&.help-text {
			display: block;
			animation: text_color_change 1s infinite alternate;
			font-size: 20px;
			font-weight: @bold;
			left: 105%;
			top: 2px;
			position: absolute;
			white-space: nowrap;
		}
	}
}
.header {
	border-bottom: 1px solid rgba(0, 0, 0, 0.2);
	padding: 5px 10px;
	height: 46px;
	position: fixed;
	width: 100%;
	top: 0;
	left: 0;
	background-color: #fff;
	z-index: 10;

	&.fade {
		.opacity(0.35);
	}

	&.force-to-dist {
		background: url("../assets/img/rank-caddy-logo2.svg");

		&.two {
			background: url("../assets/img/rectangle-slider.svg");
		}
	}

	.logo {
		background: url("../assets/img/rank-caddy-logo.svg") -17px -19px no-repeat;
		background-size: 200px auto;
		padding: 0;
		margin: 0;
		width: 200px;
		height: 36px;
		font-family: "Montserrat", sans-serif;
		letter-spacing: -2px;
		font-size: 18px;
		display: inline-block;
		text-indent: -9999px;
	}

	.search {
		margin: 0;
		position: absolute;
		left: 90px;
		top: 15px;
		height: 35px;
		overflow: hidden;
		display: none;
		width: 300px;
	}

	#toggle-menu {
		z-index: 3;
		display: inline-block;
		background: none;
		border: none;
		cursor: pointer;
		padding: 0;
		.trans;
		color: @darkContrast;
		position: absolute;
		width: 22px;
		top: 13px;
		right: 16px;
		height: 17px;
		outline: none;
		span {
			&.text {
				display: none;
			}

			&.bars {
				background: @darkContrast none repeat scroll 0 0;
				height: 2px;
				position: absolute;
				transition: all 0.3s ease 0s;
				width: 22px;
				right: 0;
				top: 8px;
				.rounded(5px);

				&:before,
				&:after {
					background: @darkContrast none repeat scroll 0 0;
					content: "";
					display: inline-block;
					height: 2px;
					left: 0;
					position: absolute;
					transform-origin: 2.5px center 0;
					.trans;
					width: 22px;
					top: -7px;
					border-radius: 5px;
				}

				&:after {
					top: 7px;
					width: 14px;
					left: 8px;
				}
			}
		}
	}

	.menu {
		padding: 0;
		position: absolute;
		left: 0;
		width: 100%;
		top: 45px;
		display: none;
		background-color: #fff;
		border-bottom: 2px solid #3b445b;
		height: calc(100vh - 45px);
		height: calc(100svh - 45px);
		overflow-x: auto;

		hr {
			margin: 0.5em 0;
		}

		.menu-item {
			margin: 0 0 5px;
			position: relative;

			> a,
			> span,
			.menu-link {
				color: #3b445b;
				text-decoration: none;
				background-position: 10px center;
				background-repeat: no-repeat;
				display: block;
				background-size: 18px auto;
				padding: 12px 0 12px 62px;
				.trans(background-color 0.25s linear);
				position: relative;
				cursor: pointer;

				svg,
				.svg {
					width: 22px;
					height: 22px;
					position: absolute;
					left: 20px;
					top: 50%;
					margin-top: -11px;
				}

				.svg {
					width: 24px;
				}

				&:before {
					position: absolute;
					width: 4px;
					height: 100%;
					left: 0;
					top: 0;
					content: "";
					display: block;
					.rounded(0px 4px 4px 0);
				}

				&.router-link-active {
					background-color: @bgGrey;

					&:before {
						background-color: @formDefaultColour;
					}
				}
			}

			&.account {
				.initials {
					display: none;
				}
			}

			&.last-mobile {
				margin-bottom: 0;
			}

			&.minmax {
				display: none;
			}

			.menu-sub-items {
				display: none;
			}
		}

		.name-info {
			padding: 12px 0 12px 62px;
			position: relative;
			cursor: pointer;

			svg,
			.svg {
				width: 22px;
				height: 22px;
				position: absolute;
				left: -42px;
				top: 50%;
				margin-top: -11px;
			}

			.name {
				display: none;
			}
		}

		.menu-popout {
			.link-wrap {
				position: relative;
				margin-bottom: 26px;

				&:last-child {
					margin-bottom: 15px;
				}

				a {
					text-decoration: none;
				}
			}
		}
	}
}

body {
	.showMenu {
		.header {
			#toggle-menu {
				span {
					&.bars {
						background: none;

						&:before,
						&:after {
							top: 0;
							background: @darkContrast;
							transform-origin: 50% 50% 0;
							width: 22px;
							transform: rotate3d(0, 0, 1, 45deg);
							.rounded(5px);
						}
						&:after {
							width: 22px;
							left: 0;
							transform: rotate3d(0, 0, 1, -45deg);
						}
					}
				}
			}

			.menu {
				display: block;
				z-index: 9;
			}
		}

		.wrapper,
		.footer {
			display: none;
		}
	}
}

.subMenuClick .menu-sub-items {
	display: none !important;
}

@media screen and (max-width: 767px) {
	.header {
		.menu {
			.menu-item {
				&.show-insights-menu-mobile {
					.menu-sub-items {
						display: block;
					}
				}
			}
		}
	}
}

@media screen and (min-width: 768px) {
	.header {
		text-align: left;
		border: none;
		background-color: rgba(0, 0, 0, 0.7);
		width: 66px;
		padding: 0;
		min-height: @minHeightDesktop;
		height: 100%;
		position: absolute;
		left: 0;

		.search {
			display: block;
		}

		.logo {
			.indent;
			background: #fff url("../assets/img/rank-caddy-logo.svg") 6px -14px no-repeat;
			position: fixed;
			z-index: 3;
			padding: 0;
			background-size: 250px auto;
			width: 250px;
			height: 63px;
			left: 10px;
			margin-left: -18px;
			top: 0;
		}

		#toggle-menu {
			display: none;
		}

		.menu {
			width: auto;
			top: 0;
			padding: 60px 0 0;
			display: block;
			min-height: 100%;
			overflow: visible;
			height: auto;
			text-align: left;
			position: relative;
			z-index: 1 !important;
			border: none;
			border-right: 1px solid @borderColor;

			.menu-item {
				margin: 4px 0 0;

				> a,
				> span,
				.menu-link {
					font-size: 9px;
					background-size: 29px auto;
					background-position: center 0;
					text-align: center;
					padding: 30px 0 0;

					svg,
					.svg {
						width: 20px;
						height: 20px;
						top: 0;
						left: 14px;
					}

					.svg {
						width: 24px;
						left: 12px;

						&.insights {
							height: 26px;
							width: 26px;
						}
					}

					.label {
						display: none;
						text-align: left;
						padding: 15px 0 0 45px;
						font-size: 16px;
					}

					&:before {
						display: none;
					}

					&.router-link-active,
					&:hover {
						border: none;
						background-color: #f0f3f2;
						.opacity(1);
					}
				}

				&.profile {
					> a {
						padding-top: 32px;
					}
				}

				&.two-line {
					margin-bottom: 10px;

					> a {
						.label {
							position: relative;
							top: -9px;
							line-height: 1.2em;
						}
					}
				}

				&.clipboard {
					> a {
						padding-top: 32px;
					}
				}

				&.handshake {
					> a {
						padding-top: 23px;

						svg {
							height: 22px;
						}
					}
				}

				&.dashboard {
					> a {
						padding-top: 32px;

						&.router-link-exact-active {
							border: none;
							background-color: transparent;
							.opacity(1);
						}
					}
				}

				&.forms {
					> a {
						padding-top: 33px;
					}
				}

				&.help {
					margin-bottom: 5px;

					> a {
						background-size: 34px auto;
						padding: 0;
						background-position: center center;
					}
				}

				&.emails {
					> a {
						background-size: 26px auto;
						background-position: center 0;
						padding-top: 30px;
					}
				}

				&.archives {
					> a {
						background-size: 26px auto;
						background-position: center 0;
						padding-top: 30px;
					}
				}

				&.matching {
					margin-top: 15px;

					> a {
						padding-top: 31px;
						background-size: 31px auto;
					}
				}

				&.minmax {
					display: block;
				}

				&:hover {
					> a,
					> span,
					.menu-link {
						background-color: rgba(240, 243, 242, 0.5);
					}

					.menu-sub-items {
						display: block;
					}
				}
			}

			&.mini {
				.menu-item {
					&:not(.has-sub-menu) {
						.label {
							position: absolute;
							background-color: @formDefaultColour;
							color: #fff;
							left: 53px;
							padding: 6px 10px;
							top: 9px;
							border-radius: 3px;
							white-space: nowrap;
							.box-shadow(1px 1px 25px -7px fade(@formDefaultColour, 50%));
							&:before {
								width: 0;
								height: 0;
								border-top: 7px solid transparent;
								border-bottom: 7px solid transparent;
								border-right: 10px solid @formDefaultColour;
								content: "";
								position: absolute;
								left: -10px;
								top: 50%;
								margin-top: -7px;
							}
						}
					}
				}
			}

			.action-menu {
				min-height: 490px;
				padding-left: 8px;

				.menu-item {
					svg,
					.svg {
						margin-top: 15px;

						&.insights {
							margin-top: 12px;
						}
					}

					> a,
					> span,
					.menu-link {
						padding: 0;
						height: 50px;
						width: 50px;
						.rounded(8px);
					}
				}
			}

			.admin-menu {
				position: fixed;
				top: 0;
				left: 0;
				width: 100%;
				padding-left: 67px;
				vertical-align: top;

				.admin-menu-wrap {
					background-color: #fff;
					border-bottom: 1px solid @borderColor;
					height: 64px;
					padding-top: 11px;
					text-align: right;
				}

				.name-info {
					display: inline-block;
					vertical-align: top;
					padding: 9px 0 0;
					margin-right: 30px;
					position: relative;

					svg,
					.svg {
						position: static;
						left: auto;
						top: auto;
						width: 18px;
						height: 18px;
					}

					.name {
						color: @darkContrast;
						font-size: 14px;
						display: inline-block;
						position: relative;
						padding-right: 16px;

						&:after {
							content: "";
							background: url("../assets/img/down-arrow.svg") center center no-repeat;
							position: absolute;
							width: 10px;
							height: 10px;
							right: 0;
							top: 50%;
							margin-top: -5px;
							transition: transform 0.35s ease-in-out;
						}

						&:hover {
							&:after {
								transform: rotate(360deg);
							}
						}
					}
				}

				.menu-item {
					text-align: center;
					margin: 0 15px 0 0;
					display: inline-block;

					> a,
					> span,
					.menu-link {
						.indent;
						display: inline-block;
						width: 34px;
						height: 34px;
						font-size: 12px;

						&:hover {
							background-color: transparent;
						}
					}

					&.help {
						margin-bottom: 0;
					}

					&.account {
						.initials {
							display: inline-block;
							background: none;
							background-color: @formDefaultColour;
							.rounded(50px);
							color: #fff;
							.opacity(1);
							text-indent: 0;
							text-align: center;
							width: 40px;
							height: 31px;
							padding: 9px 0 0;
							font-size: 16px;
							display: inline-block;
							cursor: default;
							text-transform: uppercase;
						}

						> a,
						> span,
						.menu-link {
							svg,
							.svg {
								display: none;
							}

							.label {
								display: inline;
								font-size: 14px;
							}
						}
					}
				}
			}

			.menu-popout {
				position: absolute;
				background-color: #fff;
				border: 1px solid @borderColor;
				right: 0;
				top: 46px;
				width: 180px;
				padding: 6px 12px 2px;
				.rounded(5px);
				display: none;
				.panelShadow;
				text-align: left;
				cursor: default;

				&.show {
					display: block;
				}

				&:after {
					content: "";
					position: absolute;
					width: 0;
					height: 0;
					box-sizing: border-box;
					border: 5px solid black;
					border-color: transparent transparent #fff #fff;
					-webkit-transform-origin: 0 0;
					transform-origin: 0 0;
					-webkit-transform: rotate(135deg);
					transform: rotate(135deg);
					box-shadow: -2px 3px 3px 0 rgba(0, 0, 0, 0.03), -1px 1px 0 0.5px rgba(0, 0, 0, 0.06);
					background-clip: padding-box;
					top: 0px;
					right: 7px;
				}

				hr {
					margin: 5px auto 8px;
					width: 50%;
				}

				.link-wrap {
					margin-bottom: 5px;

					&:last-child {
						margin-bottom: 0;
					}

					svg {
						left: 20px;
					}

					> a,
					> span,
					.menu-link {
						text-indent: 0;
						width: auto;
						display: inline-block;
						color: #000;
						padding: 0;
						height: auto;
						.opacity(0.6);
						cursor: pointer;
						.trans;
						text-decoration: none !important;

						&:hover {
							.opacity(1);
						}

						.label {
							padding: 0 0 0 6px;
							font-size: 14px;
							vertical-align: top;
						}
					}
				}
			}
		}
	}

	body {
		.showMenu {
			.wrapper,
			.footer {
				display: block;
			}
		}
	}

	.fullMenu {
		header {
			width: @fullMenuWidth;

			.search {
				left: @fullMenuWidth + 22px;
			}

			.menu {
				.action-menu {
					.menu-item {
						width: calc(100% + 1px);

						> a,
						> span,
						.menu-link {
							width: auto;
							border-radius: 8px 0 0 8px;

							.label {
								display: block;
							}
						}
					}
				}

				.admin-menu {
					padding-left: @fullMenuWidth;
				}
			}
		}
	}
}

@media screen and (min-width: 420px) {
	.shared-accounts {
		display: block;
	}
}

@media screen and (min-width: 768px) {
	.shared-accounts {
		top: 16px;
		left: 240px;
		width: 280px;

		> div {
			width: 100%;
		}
	}
}

@media screen and (min-width: 768px) and (min-height: @minHeightDesktop) {
	.header {
		position: fixed;
		left: 0;

		.menu {
			.menu-item {
				&.minmax {
					position: absolute;
					bottom: 60px;
				}
			}
		}
	}
}

@media screen and (min-width: 768px) and (pointer: fine) {
	.header {
		.menu {
			&.mini {
				.menu-item {
					&:not(.has-sub-menu) {
						&:hover {
							> a,
							> span,
							.menu-link {
								.label {
									display: block;
								}
							}
						}
					}
				}
			}
		}
	}
}

@media screen and (min-width: 1024px) {
	.header {
		.menu {
			.menu-popout {
				.link-wrap {
					> a,
					> span,
					.menu-link {
						display: block;
					}
				}
			}
		}
	}
}
</style>
